import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, takeWhile } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { environment } from 'src/environments/environment';

export interface TokenResult {
  token: string;
  expires: string;
  createdAt: string;
}

interface UserInfo {
  email: string;
  firstName: string;
}

const HUBSPOT_CONTAINER_ID = 'hubspot-messages-iframe-container';
const HIDE_CLASS = 'hide-hubspot-chat';
const SHOW_CLASS = 'show-hubspot-chat';
const TOKEN_EXPIRY_MS = 12 * 60 * 60 * 1000; // 12 hours
const TOKEN_CACHE_CLEAR_MS = 5000; // 5 seconds
const SCRIPT_CHECK_INTERVAL_MS = 100;
const SCRIPT_MAX_WAIT_MS = 10000;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class HubspotService implements OnDestroy {
  private tokenFetchInProgress$: Observable<boolean> | null = null;
  private hubspotContainerObserver: MutationObserver | null = null;
  private originalContainerDisplay: string | null = null;

  constructor(
    private api: ApiService,
    private store: Store,
    private growthBook: GrowthBookService,
  ) {}

  /**
   * Controls HubSpot chat bubble visibility based on a GrowthBook feature flag.
   */
  controlChatVisibility(featureFlagName = 'hubspot-chat-enabled', defaultValue = true): void {
    combineLatest([
      this.growthBook.initialized,
      this.growthBook.getFeature<boolean>(featureFlagName, defaultValue),
      this.growthBook.getFeature<boolean>('exclude-private-cloud-visitor-api', false),
    ]).pipe(
      filter(([isInitialized]) => isInitialized),
      untilDestroyed(this),
    ).subscribe(([, isEnabled, excludePrivateCloudFlag]) => {
      if (isEnabled) {
        this.showChat(excludePrivateCloudFlag);
      } else {
        this.hideChat();
      }
    });
  }

  /**
   * Check if the token is expired (12 hours)
   */
  isTokenExpired(tokenCreationTime: number): boolean {
    return Date.now() - tokenCreationTime >= TOKEN_EXPIRY_MS;
  }

  /**
   * Create a visitor token via HubSpot API
   */
  fetchCreateVisitorTokenHubspot(email: string, firstName: string, lastName: string): Observable<{ data: TokenResult }> | undefined {
    if (!environment.env.TD_APP_HUBSPOT_TOKEN_GENERATE) {
      return undefined;
    }

    if (!email || !firstName) {
      Sentry.captureException(new Error('Email or firstName is empty or undefined'));
      return undefined;
    }

    try {
      return this.api.request<{ data: TokenResult }>(
        'post',
        environment.env.TD_APP_HUBSPOT_TOKEN_GENERATE,
        { email, firstName, lastName },
      );
    } catch (error) {
      Sentry.captureException(error);
      return undefined;
    }
  }

  /**
   * @deprecated Use controlChatVisibility() instead
   */
  featureExcludePrivateCloudVisitorApi(): void {
    Sentry.captureMessage(
      '[HubSpot] featureExcludePrivateCloudVisitorApi is deprecated - use controlChatVisibility()',
      {
        level: 'warning',
        tags: { component: 'HubspotService', method: 'featureExcludePrivateCloudVisitorApi' },
      },
    );
  }

  ngOnDestroy(): void {
    this.cleanupObserver();
  }

  // ============ Private Methods ============

  private showChat(requiresUserInfo: boolean): void {
    this.cleanupObserver();
    // Remove hidden styles immediately when flag is true
    this.removeChatHiddenStyles();

    if (!this.isScriptLoaded()) {
      this.waitAndInitialize(requiresUserInfo);
    } else {
      this.initializeWidget(requiresUserInfo);
    }
  }

  /**
   * Hides the HubSpot chat bubble immediately.
   * This can be called externally to ensure chat is hidden before flag evaluation.
   */
  hideChat(): void {
    document.body.classList.remove(SHOW_CLASS);
    document.body.classList.add(HIDE_CLASS);
    this.hideContainer();
  }

  private hideContainer(): void {
    const container = this.getContainer();
    if (container) {
      // Store original display value if not already stored
      if (this.originalContainerDisplay === null && container.style.display && container.style.display !== 'none') {
        this.originalContainerDisplay = container.style.display;
      }
      container.style.display = 'none';
    } else {
      this.observeAndHideContainer();
    }
  }

  private removeChatHiddenStyles(): void {
    document.body.classList.remove(HIDE_CLASS);
    document.body.classList.add(SHOW_CLASS);

    const container = this.getContainer();
    if (container?.style.display === 'none') {
      // Restore original display value if we stored it, otherwise use empty string
      container.style.display = this.originalContainerDisplay || '';
      this.originalContainerDisplay = null;
    }
  }

  private waitAndInitialize(requiresUserInfo: boolean): void {
    if (!environment.hubspotKey) {
      return;
    }

    this.waitForHubSpotScript().pipe(
      untilDestroyed(this),
    ).subscribe(loaded => {
      if (loaded) {
        this.initializeWidget(requiresUserInfo);
      }
    });
  }

  private initializeWidget(requiresUserInfo: boolean): void {
    const hasUserSettings = this.hasExistingUserSettings();

    if (hasUserSettings) {
      this.loadWidget();
    } else if (requiresUserInfo) {
      this.setupUserInfoAndLoad();
    } else {
      this.loadWidget();
    }
  }

  private setupUserInfoAndLoad(): void {
    this.sendInfoToHubspotChat().pipe(
      untilDestroyed(this),
    ).subscribe(ready => {
      if (ready) {
        this.loadWidget();
      }
    });
  }

  private loadWidget(): void {
    if (!environment.env.TD_APP_HUBSPOT_FLOW_ID) {
      return;
    }

    if (!this.isScriptLoaded()) {
      if (!environment.hubspotKey) {
        return;
      }
      this.waitForHubSpotScript().pipe(
        untilDestroyed(this),
      ).subscribe(loaded => {
        if (loaded) {
          this.doLoadWidget();
        }
      });
      return;
    }

    this.doLoadWidget();
  }

  private doLoadWidget(): void {
    window.HubSpotConversations?.widget?.load({
      flowId: environment.env.TD_APP_HUBSPOT_FLOW_ID,
    });
  }

  private sendInfoToHubspotChat(): Observable<boolean> {
    if (this.tokenFetchInProgress$) {
      return this.tokenFetchInProgress$;
    }

    const tokenFetch$ = this.store.select(AuthState.user).pipe(
      filter(user => !!user),
      take(1),
      switchMap(user => this.processUserForHubspot(user)),
      shareReplay(1),
    );

    this.tokenFetchInProgress$ = tokenFetch$;
    this.scheduleTokenCacheClear();

    return this.tokenFetchInProgress$;
  }

  private processUserForHubspot(user: { email: string, name: string }): Observable<boolean> {
    const userInfo: UserInfo = { email: user.email, firstName: user.name };

    if (!this.isValidUser(userInfo)) {
      return of(false);
    }

    const cachedToken = this.getCachedToken();
    if (cachedToken) {
      this.setConversationSettings(userInfo.email, cachedToken);
      return of(true);
    }

    return this.fetchAndSetToken(userInfo);
  }

  private isValidUser(userInfo: UserInfo): boolean {
    return !!(userInfo.email && userInfo.firstName && !userInfo.email.includes('test+automation'));
  }

  private getCachedToken(): string | null {
    const token = localStorage.getItem('hubspotToken');
    const creationTime = parseInt(localStorage.getItem('hubspotTokenCreationTime') || '0', 10);

    if (token && !this.isTokenExpired(creationTime)) {
      return token;
    }
    return null;
  }

  private fetchAndSetToken(userInfo: UserInfo): Observable<boolean> {
    window.hsConversationsSettings = { loadImmediately: false };

    const tokenRequest = this.fetchCreateVisitorTokenHubspot(userInfo.email, userInfo.firstName, '');
    if (!tokenRequest) {
      this.setConversationSettings(userInfo.email);
      return of(true);
    }

    return tokenRequest.pipe(
      map(res => {
        const token = res?.data?.token;
        if (token) {
          this.cacheToken(token);
          this.setConversationSettings(userInfo.email, token);
        } else {
          this.setConversationSettings(userInfo.email);
        }
        return true;
      }),
      catchError(() => {
        this.setConversationSettings(userInfo.email);
        return of(true);
      }),
    );
  }

  private setConversationSettings(email: string, token?: string): void {
    window.hsConversationsSettings = {
      identificationEmail: email,
      ...(token && { identificationToken: token }),
    };
  }

  private cacheToken(token: string): void {
    localStorage.setItem('hubspotToken', token);
    localStorage.setItem('hubspotTokenCreationTime', Date.now().toString());
  }

  private scheduleTokenCacheClear(): void {
    setTimeout(() => {
      this.tokenFetchInProgress$ = null;
    }, TOKEN_CACHE_CLEAR_MS);
  }

  private waitForHubSpotScript(maxWaitTime = SCRIPT_MAX_WAIT_MS): Observable<boolean> {
    const maxChecks = Math.floor(maxWaitTime / SCRIPT_CHECK_INTERVAL_MS);

    return timer(0, SCRIPT_CHECK_INTERVAL_MS).pipe(
      map(() => this.isScriptLoaded()),
      takeWhile(isLoaded => !isLoaded, true),
      filter((isLoaded, index) => isLoaded || index >= maxChecks),
      take(1),
    );
  }

  private observeAndHideContainer(): void {
    this.cleanupObserver();

    this.hubspotContainerObserver = new MutationObserver(() => {
      const container = this.getContainer();
      if (container) {
        container.style.display = 'none';
        this.cleanupObserver();
      }
    });

    this.hubspotContainerObserver.observe(document.body, { childList: true, subtree: true });
  }

  private cleanupObserver(): void {
    if (this.hubspotContainerObserver) {
      this.hubspotContainerObserver.disconnect();
      this.hubspotContainerObserver = null;
    }
  }

  private getContainer(): HTMLElement | null {
    return document.querySelector<HTMLElement>(`#${HUBSPOT_CONTAINER_ID}`);
  }

  private isScriptLoaded(): boolean {
    return !!window.HubSpotConversations;
  }

  private hasExistingUserSettings(): boolean {
    const settings = window.hsConversationsSettings;
    return !!(settings?.identificationEmail || settings?.identificationToken);
  }
}
